import React, { useState, useCallback, useEffect } from 'react';
import { TextField, Typography, Box, Stack, Checkbox } from '@mui/material';
import ActionButton from '../../../core/components/ui/ActionButton';
import { TermsText } from '../../../core/theme/formComponents';
import { CheckBox } from '@mui/icons-material';

interface VerificationCodeStepProps {
  activeTab: 'login' | 'signup';
  contactValue: string;
  isVerifyingCode: boolean;
  hasVerifyCodeError: boolean;
  onVerifyCode: (code: string) => void;
  onResendCode: () => void;
  onChangeContactValue: (arg:string) => void;
  verificationType?: 'phone' | 'email';
  verificationHeading?: string;
  verificationDescription?: string;
  buttonText?: string;
}

// const StyledLink = styled(Link)(({ theme }) => ({
//   color: '#5C5AF0',
//   textDecoration: 'none',
//   '&:hover': {
//     textDecoration: 'underline',
//   },
// }));

const VerificationCodeStep: React.FC<VerificationCodeStepProps> = ({
  activeTab,
  contactValue,
  isVerifyingCode,
  hasVerifyCodeError,
  onVerifyCode,
  onResendCode,
  onChangeContactValue,
  verificationType = 'phone',
  verificationHeading,
  verificationDescription,
  buttonText,
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(60); // 5 minutes in seconds
  const [isUserConsent, setUserConsent] = useState(false)
  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prevTime => prevTime - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleVerificationCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    // Only allow digits
    const digitsOnly = input.replace(/\D/g, '');
    setVerificationCode(digitsOnly);
  }, []);

  const handleVerifyCode = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onVerifyCode(verificationCode);
  }, [verificationCode, onVerifyCode]);

  const handleResendCode = useCallback(() => {
    onResendCode();
    setTimeLeft(60);// Reset timer to 1 minutes
  }, [onResendCode]);

  const defaultHeading = verificationType === 'phone'
    ? 'Verify Your Phone'
    : 'Verify Your Email';

  const defaultDescription = `Enter the 6-digit code sent to ${contactValue}`;

  const defaultButtonText = (() => {
    if (buttonText) return buttonText;
    if (activeTab === 'login') return 'Log In';
    if (verificationType === 'email') return 'Complete Sign Up';
    return verificationType === 'phone' ? 'Verify Phone' : 'Verify Email';
  })();

  const handleConsent = () => {
    setUserConsent(!isUserConsent);
  }

  const changeLabel = verificationType === 'phone'
    ? '← Change phone number'
    : '← Change email';
  const isVerifyDisabled = isVerifyingCode || verificationCode.length < 6 || (verificationType === 'email' && activeTab === 'signup' && !isUserConsent);
  return (
    <form onSubmit={handleVerifyCode}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {verificationHeading || defaultHeading}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {verificationDescription || defaultDescription}
        </Typography>
      </Box>

      <Box className="verification-input" sx={{ mb: 3 }}>
        <TextField
          fullWidth
          id="verification-code"
          placeholder="• • • • • •"
          variant="outlined"
          value={verificationCode}
          onChange={handleVerificationCodeChange}
          sx={{
            '& .MuiOutlinedInput-input': {
              textAlign: 'center',
              letterSpacing: '0.5em',
              fontSize: '1.5rem'
            },
          }}
          slotProps={{
            htmlInput: {
              maxLength: 6,
              autoFocus: true,
              inputMode: 'numeric',
              pattern: '[0-9]*'
            }
          }}
          data-testid="verification-code-input"
          error={hasVerifyCodeError}
          style={{
            marginBottom: 5
          }}
        />
        <Box className="verification-options">
          {(verificationType === 'phone' || verificationType === 'email') && (
            <Typography
              variant="body2"
              sx={{
                color: (theme) => theme.palette.info.main,
                cursor: 'pointer',
                fontSize: '0.85rem',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              onClick={() => onChangeContactValue(verificationType)}
            >
              {changeLabel}
            </Typography>
          )}
        </Box>
        {activeTab === 'signup' && verificationType === 'email' && <Stack direction={'row'} alignItems={'start'} gap={2} mt={3}>
          <Checkbox style={{ padding: 0, marginTop: 2 }} onClick={handleConsent} checked={isUserConsent} />
          <TermsText>I consent to receiving messages from MindRight Health</TermsText>
        </Stack>}
      </Box>
      <ActionButton
        text={defaultButtonText}
        loading={isVerifyingCode}
        disabled={isVerifyDisabled}
        //onClick={handleVerifyCode}
        type='submit'
        testId="verify-button"
      />
      <Typography variant="body2" align="center" sx={{ mb: 3, mt: 3 }}>Did't receive code? {formatTime(timeLeft) === "00:00" ? <Typography component={'span'} onClick={handleResendCode} variant="body2"
        sx={{
          color: (theme) => theme.palette.primary[500],
          cursor: 'pointer',
          fontSize: '0.85rem',
          marginLeft: verificationType === 'email' ? 'auto' : 'inherit',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}>Resend Code</Typography> : <Typography
          variant="body2" color={'textDisabled'} component={'span'} sx={{
            cursor: 'pointer',
          }}>Resend code
        {' '}<Typography variant="body2" component={'span'} color='primary' style={{ cursor: 'default' }}>{formatTime(timeLeft)}s</Typography></Typography>} </Typography>


    </form>
  );
};

export default React.memo(VerificationCodeStep);